import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';

const TopPagePathsChart = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [timePeriod, setTimePeriod] = useState('7days');
  const [pagePathsData, setPagePathsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Format duration from seconds to readable format
  const formatDuration = (seconds) => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs > 0 ? `${secs}s` : ''}`;
    }
    return `${Math.floor(seconds)}s`;
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div 
          className="bg-white p-3 border shadow-lg rounded"
          style={{ 
            borderColor: styles.borderColor,
            fontFamily: 'Raleway, sans-serif'
          }}
        >
          <p className="text-sm font-medium mb-2" style={{ color: styles.textPrimary }}>
            {data.path || 'Unknown Path'}
          </p>
          <p className="text-xs" style={{ color: styles.textSecondary }}>
            Count: <span style={{ color: styles.textPrimary }}>{data.visitCount?.toLocaleString() || 0}</span>
          </p>
          <p className="text-xs" style={{ color: styles.textSecondary }}>
            Avg Duration: <span style={{ color: styles.textPrimary }}>{formatDuration(data.avgDuration || 0)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Fetch top page paths data
  const fetchTopPagePaths = useCallback(async () => {
    if (!teamId || parentLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const params = { 
        team_id: teamId,
        timePeriod
      };

      // Add website filter if selected
      if (appliedFilters.websites && appliedFilters.websites.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      const response = await axiosInstance.get('/overview/top-page-paths', { params });

      if (response.data?.success) {
        setPagePathsData(response.data.data || []);
      } else {
        console.warn('Top page paths response not successful:', response.data);
        setPagePathsData([]);
      }
    } catch (error) {
      console.error('Error fetching top page paths:', error);
      setPagePathsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [teamId, appliedFilters.websites, timePeriod, parentLoading]);

  useEffect(() => {
    fetchTopPagePaths();
  }, [fetchTopPagePaths]);

  // Time Period Selector Component
  const TimePeriodSelector = () => {
    const periods = [
      { value: '24hrs', label: 'Last 24hrs' },
      { value: '7days', label: 'Last 7 days' },
      { value: '30days', label: 'Last 30 days' },
      { value: '90days', label: 'Last 90 days' },
      { value: '1year', label: 'Last year' }
    ];

    return (
      <div className="flex gap-1 flex-wrap">
        {periods.map((period) => (
          <button
            key={period.value}
            onClick={() => setTimePeriod(period.value)}
            className="time-selector-normal px-3 py-1 text-xs transition-colors border"
            style={{ 
              fontFamily: 'Raleway, sans-serif',
              backgroundColor: timePeriod === period.value ? styles.accentColor : styles.primaryBg,
              color: timePeriod === period.value ? '#ffffff' : styles.textPrimary,
              borderColor: styles.borderColor
            }}
          >
            {period.label}
          </button>
        ))}
      </div>
    );
  };

  // Truncate long paths for display
  const truncatePath = (path, maxLength = 30) => {
    if (!path) return '';
    if (path.length <= maxLength) return path;
    return path.substring(0, maxLength - 3) + '...';
  };

  if (isLoading || parentLoading) {
    return (
      <div 
        className="p-4 md:p-6"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading page paths data...
          </div>
        </div>
      </div>
    );
  }

  if (!pagePathsData || pagePathsData.length === 0) {
    return (
      <div 
        className="p-4 md:p-6"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No page paths data available
          </div>
        </div>
      </div>
    );
  }

  // Transform data for chart
  const chartData = pagePathsData.map(item => ({
    path: item.path,
    displayPath: truncatePath(item.path),
    visitCount: item.visitCount || 0,
    avgDuration: item.avgDuration || 0
  }));

  return (
    <div 
      className="p-4 md:p-6"
      style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
    >
      {/* Title and Time Period Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <h3 
          className="text-sm font-medium"
          style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
        >
          Top Page Paths
        </h3>
        <TimePeriodSelector />
      </div>

      {/* Chart */}
      <div style={{ height: '400px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} horizontal={true} />
            <XAxis 
              dataKey="displayPath"
              tick={{ 
                fill: styles.textPrimary, 
                fontSize: 11,
                fontFamily: 'Raleway, sans-serif'
              }}
              axisLine={{ stroke: styles.borderColor }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis 
              tick={{ 
                fill: styles.textPrimary, 
                fontSize: 11,
                fontFamily: 'Raleway, sans-serif'
              }}
              axisLine={{ stroke: styles.borderColor }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="visitCount" 
              fill={styles.accentColor}
              name="Visits"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TopPagePathsChart;

